import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Progress } from "../../ui/progress";
import { Alert, AlertDescription } from "../../ui/alert";
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  Clipboard,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { cleanText, extractMedicationDetails } from "../../../utils/ocrUtils";
import { prechartingService } from "../../../apis/prechartingService";
import { Textarea } from "../../ui/textarea";

const TextExtractModal = ({ isOpen, onClose, onExtractComplete }) => {
  const [text, setText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [extractedMedications, setExtractedMedications] = useState([]);
  const [isComplete, setIsComplete] = useState(false);

  const handleExtract = async () => {
    if (!text || text.trim().length === 0) {
      toast.error("Please paste some text to extract from");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setStatus("Extracting from text...");
    setExtractedMedications([]);
    setIsComplete(false);

    try {
      await handleExtractMedications([text]);
    } catch (error) {
      console.error("Error during text extraction:", error);
      toast.error("Failed to extract medications. Please try again.");
      setStatus("Error occurred during extraction");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExtractMedications = async (texts) => {
    setStatus("Extracting medications from text...");
    setProgress(50);

    // Process all extracted text to get structured medication details
    const allMedicationDetails = [];
    for (const text of texts) {
      const cleanedText = cleanText(text);
      const medications = extractMedicationDetails(cleanedText);
      allMedicationDetails.push(...medications);
    }

    // Remove duplicates based on medication name (case-insensitive)
    const uniqueMedicationsMap = new Map();
    for (const med of allMedicationDetails) {
      const key = med.name.toLowerCase();
      if (!uniqueMedicationsMap.has(key)) {
        uniqueMedicationsMap.set(key, med);
      } else {
        // Merge details if duplicate found (prefer more complete information)
        const existing = uniqueMedicationsMap.get(key);
        if (!existing.dosage && med.dosage) existing.dosage = med.dosage;
        if (!existing.form && med.form) existing.form = med.form;
        if (!existing.frequency && med.frequency) existing.frequency = med.frequency;
        if (!existing.route && med.route) existing.route = med.route;
        if (!existing.instructions && med.instructions) existing.instructions = med.instructions;
      }
    }

    const uniqueMedications = Array.from(uniqueMedicationsMap.values());
    uniqueMedications.sort((a, b) => a.name.localeCompare(b.name));

    setStatus("Validating medications against database...");
    setProgress(70);

    // Validate medications against database
    const validatedMedications = await Promise.all(
      uniqueMedications.map(async (medication) => {
        try {
          const result = await prechartingService.lookupMedication(medication.name);
          if (result.success && result.data) {
            return {
              medicationName: result.data.medicationName,
              companyName: result.data.companyName || '',
              medicationType: result.data.medicationType || '',
              preventive: result.data.preventive || false,
              isValidated: true,
              isValid: true,
              dosage: medication.dosage || '',
              form: medication.form || '',
              quantity: medication.quantity || '',
              route: medication.route || '',
              frequency: medication.frequency || '',
              instructions: medication.instructions || '',
              notes: medication.notes || '',
              status: medication.status || 'taking'
            };
          } else {
            return {
              medicationName: medication.name,
              companyName: '',
              medicationType: '',
              preventive: false,
              isValidated: true,
              isValid: false,
              dosage: medication.dosage || '',
              form: medication.form || '',
              quantity: medication.quantity || '',
              route: medication.route || '',
              frequency: medication.frequency || '',
              instructions: medication.instructions || '',
              notes: medication.notes || '',
              status: medication.status || 'taking'
            };
          }
        } catch (error) {
          console.error(`Error validating medication ${medication.name}:`, error);
          return {
            medicationName: medication.name,
            companyName: '',
            medicationType: '',
            preventive: false,
            isValidated: false,
            isValid: false,
            dosage: medication.dosage || '',
            form: medication.form || '',
            quantity: medication.quantity || '',
            route: medication.route || '',
            frequency: medication.frequency || '',
            instructions: medication.instructions || '',
            notes: medication.notes || '',
            status: medication.status || 'taking'
          };
        }
      })
    );

    // Include all medications (both validated and unvalidated)
    const allMedications = validatedMedications;
    const validCount = allMedications.filter(m => m.isValidated && m.isValid).length;
    const unvalidatedCount = allMedications.filter(m => !m.isValidated || !m.isValid).length;
    
    setExtractedMedications(allMedications);
    setProgress(100);
    setStatus("Extraction complete!");
    setIsComplete(true);

    if (allMedications.length > 0) {
      if (validCount > 0 && unvalidatedCount > 0) {
        toast.success(
          `Successfully extracted ${validCount} validated and ${unvalidatedCount} unverified medications with details.`
        );
      } else if (validCount > 0) {
        toast.success(
          `Successfully extracted ${validCount} validated medications with details.`
        );
      } else {
        toast.success(
          `Extracted ${unvalidatedCount} unverified medications. Please review and validate them.`
        );
      }
    } else {
      toast.success("Extraction complete, but no medications found.");
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setText("");
      setExtractedMedications([]);
      setIsComplete(false);
      setProgress(0);
      setStatus("");
      onClose();
    }
  };

  const handleAddToForm = () => {
    if (extractedMedications.length > 0) {
      onExtractComplete(extractedMedications);
      handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5 text-blue-500" />
            Extract from Text
          </DialogTitle>
          <DialogDescription>
            Paste medication list text to extract medications. All processing is done locally on your device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
              {/* Text Input Area */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Paste medication list text:</label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste your medication list here...&#10;&#10;Example:&#10;Current Medications&#10;Taking&#10;metoprolol 25 mg tablet, extended release 1 tab(s) orally once a day&#10;Entresto 24 mg-26 mg tablet 1 tab(s) orally 2 times a day"
                  className="min-h-[200px] font-mono text-sm"
                  disabled={isProcessing}
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{text.length} characters</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setText("")}
                    disabled={isProcessing || !text}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {/* Progress */}
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{status}</span>
                    <span className="text-gray-600 font-medium">
                      {Math.round(progress)}%
                    </span>
                  </div>
                  <Progress value={progress} />
                </div>
              )}

              {/* Results */}
              {isComplete && extractedMedications.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Successfully extracted{" "}
                      <strong>{extractedMedications.length}</strong> medications
                      {extractedMedications.filter(m => m.isValidated && m.isValid).length > 0 && (
                        <> ({extractedMedications.filter(m => m.isValidated && m.isValid).length} validated, {extractedMedications.filter(m => !m.isValidated || !m.isValid).length} unverified)</>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="max-h-60 overflow-y-auto border rounded-lg p-4">
                    <div className="space-y-2">
                      {extractedMedications.map((medication, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2 p-2 rounded ${
                            medication.isValidated && medication.isValid
                              ? "bg-green-50 border border-green-200"
                              : medication.isValidated && !medication.isValid
                              ? "bg-orange-50 border border-orange-200"
                              : "bg-gray-50 border border-gray-200"
                          }`}
                        >
                          <span className="text-sm font-medium text-purple-600">
                            {medication.medicationName}
                          </span>
                          {medication.companyName && (
                            <span className="text-xs text-gray-500">
                              ({medication.companyName})
                            </span>
                          )}
                          {medication.medicationType && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {medication.medicationType}
                            </span>
                          )}
                          {medication.dosage && (
                            <span className="text-xs text-gray-600">
                              {medication.dosage}
                            </span>
                          )}
                          {medication.status && medication.status !== 'taking' && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              medication.status === 'not-taking' 
                                ? 'bg-red-100 text-red-800' 
                                : medication.status === 'discontinued'
                                ? 'bg-gray-100 text-gray-800'
                                : ''
                            }`}>
                              {medication.status === 'not-taking' ? 'Not Taking' : medication.status === 'discontinued' ? 'Discontinued' : medication.status}
                            </span>
                          )}
                          {medication.isValidated && medication.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600 ml-auto" title="Validated" />
                          ) : medication.isValidated && !medication.isValid ? (
                            <AlertCircle className="h-4 w-4 text-orange-600 ml-auto" title="Not in database" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-gray-600 ml-auto" title="Unverified" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={handleExtract}
              disabled={isProcessing || !text || text.trim().length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Clipboard className="h-4 w-4 mr-2" />
                  Extract
                </>
              )}
            </Button>
            {extractedMedications.length > 0 ? (
              <Button 
                onClick={handleAddToForm} 
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Add to Medication List
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TextExtractModal;

